import React from 'react';

const ENTERING_STATE = 'entering';
const LEAVING_STATE = 'leaving';
const NOT_VISIBLE = 'not-visible';

const LEFTWARD_DIRECTION = 'left';
const RIGHTWARD_DIRECTION = 'right';

const ENTERING_CLASS_NAME = 'delayed-swipe-entering';
const LEAVING_CLASS_NAME = 'delayed-swipe-leaving';
const NOT_VISIBLE_CLASS_NAME = 'delayed-swipe-not-visible';

const LEFTWARD_DIRECTION_CLASS_NAME = 'delayed-swipe-left';
const RIGHTWARD_DIRECTION_CLASS_NAME = 'delayed-swipe-right';

type StateClassNames = typeof ENTERING_CLASS_NAME | typeof LEAVING_CLASS_NAME | typeof NOT_VISIBLE_CLASS_NAME;
type DirectionClassNames = typeof LEFTWARD_DIRECTION_CLASS_NAME | typeof RIGHTWARD_DIRECTION_CLASS_NAME;
export type AnimationStatesType = typeof ENTERING_STATE | typeof LEAVING_STATE | typeof NOT_VISIBLE;
export type AnimationDirectionsType = typeof LEFTWARD_DIRECTION | typeof RIGHTWARD_DIRECTION;

interface AnimationState {
  state: AnimationStatesType;
}

interface AnimationDirection {
  direction: AnimationDirectionsType;
}

export interface DelayedSwipeProps extends AnimationState, AnimationDirection {
  children: JSX.Element[];
  delayMs?: number;
  animationDurationMs?: number;
}

/**
 * Each child element arranged in an array will swipe to a chosen direction in a delayed manner,
 * first element swipes, then the next one and so on, until the last one. Them they can swipe out in the same manner.
 */
export default function DelayedSwipe({
  children,
  state,
  direction = LEFTWARD_DIRECTION,
  delayMs = 400,
  animationDurationMs = 800,
}: DelayedSwipeProps): JSX.Element {
  const getStateClassName = function (state: AnimationStatesType): StateClassNames {
    switch (state) {
      case ENTERING_STATE:
        return ENTERING_CLASS_NAME;
      case LEAVING_STATE:
        return LEAVING_CLASS_NAME;
      case NOT_VISIBLE:
        return NOT_VISIBLE_CLASS_NAME;
    }
  };

  const getDirectionClassName = function (direction: AnimationDirectionsType): DirectionClassNames {
    switch (direction) {
      case LEFTWARD_DIRECTION:
        return LEFTWARD_DIRECTION_CLASS_NAME;
      case RIGHTWARD_DIRECTION:
        return RIGHTWARD_DIRECTION_CLASS_NAME;
    }
  };

  const getAnimationDelay = function (state: AnimationStatesType, index: number, length: number): number {
    switch (state) {
      case ENTERING_STATE:
      case NOT_VISIBLE:
        return delayMs * (index + 1);
      case LEAVING_STATE:
        return delayMs * (length - index);
    }
  };

  const classNames: string[] = ['delayed-swipe', getStateClassName(state), getDirectionClassName(direction)];

  return (
    <div className="delayed-swipe-container">
      {React.Children.map(children, (child, i) =>
        React.cloneElement(child, {
          style: Object.assign({}, child.props.style ? child.props.style : {}, {
            animationDelay: `${getAnimationDelay(state, i, children.length)}ms`,
            animationDuration: `${animationDurationMs - getAnimationDelay(state, i, children.length) * i + 100}ms`,
          }),
          className: `${classNames.join(' ')} ${child.props.className ? child.props.className : ''}`,
        })
      )}
    </div>
  );
}
